import { useState, useEffect, useRef } from "react";
import { X, Send, Bot, User, Copy, Check, MessageSquare, Plus, Trash2, GripVertical, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { getAIResponse } from "@/services/aiService";

interface Message {
  id: string;
  content: string;
  sender: "user" | "ai";
  timestamp: Date;
}

interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  createdAt: Date;
  updatedAt: Date;
}

interface ChatModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ChatModal({ isOpen, onClose }: ChatModalProps) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [showConversationList, setShowConversationList] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{ dragging: boolean; startX: number; startY: number; startLeft: number; startTop: number }>({ dragging: false, startX: 0, startY: 0, startLeft: 0, startTop: 0 });
  const [position, setPosition] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  const viewportRef = useRef<HTMLElement | null>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const currentConversation = conversations.find(c => c.id === currentConversationId);
  const messages = currentConversation?.messages || [];

  // Load conversations from localStorage on mount
  useEffect(() => {
    const savedConversations = localStorage.getItem("chat-conversations");
    if (savedConversations) {
      try {
        const parsed = JSON.parse(savedConversations);
        const conversations = parsed.map((conv: any) => ({
          ...conv,
          createdAt: new Date(conv.createdAt),
          updatedAt: new Date(conv.updatedAt),
          messages: conv.messages.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }))
        }));
        setConversations(conversations);
        
        // Set current conversation to the most recent one
        if (conversations.length > 0) {
          const mostRecent = conversations.sort((a: Conversation, b: Conversation) => 
            b.updatedAt.getTime() - a.updatedAt.getTime()
          )[0];
          setCurrentConversationId(mostRecent.id);
        }
      } catch (error) {
        console.error("Failed to load conversations:", error);
      }
    }
  }, []);

  // Save conversations to localStorage whenever conversations change
  useEffect(() => {
    if (conversations.length > 0) {
      localStorage.setItem("chat-conversations", JSON.stringify(conversations));
    }
  }, [conversations]);

  // Initialize draggable position (bottom-right with 16px margin), restore from localStorage
  useEffect(() => {
    if (!isOpen) return;
    const saved = localStorage.getItem("chat-modal-pos");
    const applyBottomRight = () => {
      const margin = 16; // same as p-4
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const el = wrapperRef.current;
      const w = el?.offsetWidth ?? 448; // ~max-w-md
      const h = el?.offsetHeight ?? 600;
      setPosition({ left: Math.max(margin, vw - w - margin), top: Math.max(margin, vh - h - margin) });
    };
    if (saved) {
      try {
        const { left, top } = JSON.parse(saved);
        setPosition({ left, top });
      } catch {
        applyBottomRight();
      }
    } else {
      // wait a tick to measure actual size
      setTimeout(applyBottomRight, 0);
    }
  }, [isOpen]);

  // Persist draggable position
  useEffect(() => {
    if (!isOpen) return;
    localStorage.setItem("chat-modal-pos", JSON.stringify(position));
  }, [position, isOpen]);

  // Handle drag events
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const ds = dragStateRef.current;
      if (!ds.dragging) return;
      const dx = e.clientX - ds.startX;
      const dy = e.clientY - ds.startY;
      const nextLeft = ds.startLeft + dx;
      const nextTop = ds.startTop + dy;
      const el = wrapperRef.current;
      const w = el?.offsetWidth ?? 448;
      const h = el?.offsetHeight ?? 600;
      const maxLeft = Math.max(0, window.innerWidth - w);
      const maxTop = Math.max(0, window.innerHeight - h);
      setPosition({
        left: Math.min(Math.max(0, nextLeft), maxLeft),
        top: Math.min(Math.max(0, nextTop), maxTop)
      });
    };
    const onUp = () => {
      dragStateRef.current.dragging = false;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    // No global listeners registered here by default
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  const startDrag = (e: React.PointerEvent) => {
    if (window.innerWidth < 640) return; // skip on small screens
    const ds = dragStateRef.current;
    ds.dragging = true;
    ds.startX = e.clientX;
    ds.startY = e.clientY;
    ds.startLeft = position.left;
    ds.startTop = position.top;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    const onMove = (evt: PointerEvent) => {
      const dx = evt.clientX - ds.startX;
      const dy = evt.clientY - ds.startY;
      const el = wrapperRef.current;
      const w = el?.offsetWidth ?? 448;
      const h = el?.offsetHeight ?? 600;
      const maxLeft = Math.max(0, window.innerWidth - w);
      const maxTop = Math.max(0, window.innerHeight - h);
      const nextLeft = ds.startLeft + dx;
      const nextTop = ds.startTop + dy;
      setPosition({
        left: Math.min(Math.max(0, nextLeft), maxLeft),
        top: Math.min(Math.max(0, nextTop), maxTop)
      });
    };
    const onUp = () => {
      ds.dragging = false;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    const scrollToBottom = () => {
      const viewport = viewportRef.current || (function() {
        if (!scrollAreaRef.current) return null;
        let parent = scrollAreaRef.current.parentElement;
        while (parent) {
          if (parent.hasAttribute('data-radix-scroll-area-viewport')) return parent as HTMLElement;
          parent = parent.parentElement;
        }
        return null;
      })();
      if (viewport) viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'smooth' });
    };

    // Use multiple timeouts to ensure DOM is updated
    setTimeout(scrollToBottom, 50);
    setTimeout(scrollToBottom, 150);
    setTimeout(scrollToBottom, 300);
  }, [messages, isLoading]);

  // Additional scroll trigger when conversation changes
  useEffect(() => {
    if (currentConversationId) {
      const scrollToBottom = () => {
        const viewport = viewportRef.current;
        if (viewport) viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'smooth' });
      };
      
      setTimeout(scrollToBottom, 100);
    }
  }, [currentConversationId]);

  // Wire up viewport ref and scroll listener
  useEffect(() => {
    if (!isOpen) return;
    // find viewport once
    if (!viewportRef.current && scrollAreaRef.current) {
      let parent = scrollAreaRef.current.parentElement;
      while (parent) {
        if (parent.hasAttribute('data-radix-scroll-area-viewport')) {
          viewportRef.current = parent as HTMLElement;
          break;
        }
        parent = parent.parentElement;
      }
    }
    const vp = viewportRef.current;
    if (!vp) return;
    const handleScroll = () => {
      const threshold = 80;
      const distanceFromBottom = vp.scrollHeight - (vp.scrollTop + vp.clientHeight);
      setIsAtBottom(distanceFromBottom <= threshold);
    };
    handleScroll();
    vp.addEventListener('scroll', handleScroll, { passive: true });
    return () => vp.removeEventListener('scroll', handleScroll as any);
  }, [isOpen, messages.length]);

  const scrollToBottomNow = () => {
    const vp = viewportRef.current;
    if (vp) vp.scrollTo({ top: vp.scrollHeight, behavior: 'smooth' });
  };

  const createNewConversation = () => {
    const newConversation: Conversation = {
      id: Date.now().toString(),
      title: "New Chat",
      messages: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    
    setConversations(prev => [newConversation, ...prev]);
    setCurrentConversationId(newConversation.id);
    setShowConversationList(false);
  };

  const switchConversation = (conversationId: string) => {
    setCurrentConversationId(conversationId);
    setShowConversationList(false);
  };

  const deleteConversation = (conversationId: string) => {
    setConversations(prev => prev.filter(c => c.id !== conversationId));
    if (currentConversationId === conversationId) {
      const remaining = conversations.filter(c => c.id !== conversationId);
      setCurrentConversationId(remaining.length > 0 ? remaining[0].id : null);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputValue.trim(),
      sender: "user",
      timestamp: new Date(),
    };

    // Create new conversation if none exists
    if (!currentConversationId) {
      const newConversation: Conversation = {
        id: Date.now().toString(),
        title: userMessage.content.slice(0, 30) + "...",
        messages: [userMessage],
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      
      setConversations(prev => [newConversation, ...prev]);
      setCurrentConversationId(newConversation.id);
    } else {
      // Update existing conversation with new message
      setConversations(prev => prev.map(c => 
        c.id === currentConversationId 
          ? { 
              ...c, 
              messages: [...c.messages, userMessage],
              updatedAt: new Date(),
              title: c.messages.length === 0 ? userMessage.content.slice(0, 30) + "..." : c.title
            }
          : c
      ));
    }

    setInputValue("");
    setIsLoading(true);

    // Force scroll after user message is added
    setTimeout(() => {
      if (scrollAreaRef.current) {
        let parent = scrollAreaRef.current.parentElement;
        while (parent) {
          if (parent.hasAttribute('data-radix-scroll-area-viewport')) {
            parent.scrollTop = parent.scrollHeight;
            return;
          }
          parent = parent.parentElement;
        }
      }
    }, 200);

    try {
      const aiResponse = await getAIResponse(userMessage.content);
      
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: aiResponse.content,
        sender: "ai",
        timestamp: new Date(),
      };

      setConversations(prev => prev.map(c => 
        c.id === currentConversationId 
          ? { 
              ...c, 
              messages: [...c.messages, aiMessage],
              updatedAt: new Date()
            }
          : c
      ));
    } catch (error) {
      console.error("Failed to get AI response:", error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: "Sorry, I'm having trouble responding right now. Please try again.",
        sender: "ai",
        timestamp: new Date(),
      };
      
      setConversations(prev => prev.map(c => 
        c.id === currentConversationId 
          ? { 
              ...c, 
              messages: [...c.messages, errorMessage],
              updatedAt: new Date()
            }
          : c
      ));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const copyToClipboard = async (content: string, messageId: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const clearAllChats = () => {
    setConversations([]);
    setCurrentConversationId(null);
    localStorage.removeItem("chat-conversations");
  };

  if (!isOpen) return null;

  return (
    <div
      ref={wrapperRef}
      className="fixed z-50"
      style={{ left: position.left, top: position.top }}
    >
      <Card className="w-full max-w-md h-[600px] flex flex-col shadow-2xl">
        <CardHeader
          className="flex flex-row items-center justify-between space-y-0 pb-2 select-none"
        >
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <span className="text-muted-foreground cursor-move" onPointerDown={startDrag} aria-label="Drag">
              <GripVertical className="h-4 w-4" />
            </span>
            {showConversationList ? (
              <>
                <MessageSquare className="h-5 w-5" />
                Chat History
              </>
            ) : (
              <>
                <Bot className="h-5 w-5" />
                AI Assistant
              </>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {showConversationList ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={createNewConversation}
                  className="text-xs"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  New Chat
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllChats}
                  className="text-xs"
                >
                  Clear All
                </Button>
                <Button variant="ghost" size="sm" onClick={onClose}>
                  <X className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowConversationList(true)}
                  className="text-xs"
                >
                  <MessageSquare className="h-3 w-3 mr-1" />
                  History
                </Button>
                <Button variant="ghost" size="sm" onClick={onClose}>
                  <X className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="flex-1 flex flex-col p-0 min-h-0">
          {showConversationList ? (
            // Conversation List View
            <ScrollArea className="flex-1 p-4 overflow-hidden">
              <div className="space-y-2">
                {conversations.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No conversations yet</p>
                    <Button 
                      onClick={createNewConversation}
                      className="mt-4"
                      size="sm"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Start New Chat
                    </Button>
                  </div>
                ) : (
                  conversations
                    .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
                    .map((conversation) => (
                      <div
                        key={conversation.id}
                        className={cn(
                          "flex items-center justify-between p-3 rounded-lg cursor-pointer hover:bg-accent transition-colors group",
                          currentConversationId === conversation.id && "bg-accent"
                        )}
                        onClick={() => switchConversation(conversation.id)}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {conversation.title}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {conversation.messages.length} messages
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {conversation.updatedAt.toLocaleDateString()}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteConversation(conversation.id);
                          }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))
                )}
              </div>
            </ScrollArea>
          ) : (
            // Chat View
            <>
              <div className="relative flex flex-1 min-h-0">
              <ScrollArea className="flex-1 p-4 overflow-hidden scroll-smooth">
                <div className="space-y-4 min-h-0" ref={scrollAreaRef}>
                  {messages.length === 0 && (
                    <div className="text-center text-muted-foreground py-8">
                      <Bot className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Start a conversation with AI</p>
                      {!currentConversationId && (
                        <Button 
                          onClick={createNewConversation}
                          className="mt-4"
                          size="sm"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Start New Chat
                        </Button>
                      )}
                    </div>
                  )}
                  
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={cn(
                        "flex gap-3",
                        message.sender === "user" ? "justify-end" : "justify-start"
                      )}
                    >
                      {message.sender === "ai" && (
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <Bot className="h-4 w-4 text-primary" />
                          </div>
                        </div>
                      )}
                      
                  <div
                    className={cn(
                      "max-w-[80%] rounded-lg px-3 py-2 text-sm break-words",
                      message.sender === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted"
                    )}
                  >
                    <p className="whitespace-pre-wrap break-words overflow-wrap-anywhere">{message.content}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs opacity-70">
                            {message.timestamp.toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                          {message.sender === "ai" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 opacity-70 hover:opacity-100"
                              onClick={() => copyToClipboard(message.content, message.id)}
                            >
                              {copiedMessageId === message.id ? (
                                <Check className="h-3 w-3 text-green-500" />
                              ) : (
                                <Copy className="h-3 w-3" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {message.sender === "user" && (
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                            <User className="h-4 w-4 text-primary-foreground" />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {isLoading && (
                    <div className="flex gap-3 justify-start">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <Bot className="h-4 w-4 text-primary" />
                        </div>
                      </div>
                      <div className="bg-muted rounded-lg px-3 py-2 text-sm">
                        <div className="flex items-center gap-2">
                          <div className="flex gap-1">
                            <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" />
                            <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "0.1s" }} />
                            <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "0.2s" }} />
                          </div>
                          <span className="text-muted-foreground">AI is typing...</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
              {!isAtBottom && (
                <button
                  onClick={scrollToBottomNow}
                  className="absolute right-4 bottom-4 h-8 w-8 rounded-full bg-primary text-primary-foreground shadow-md flex items-center justify-center transition-opacity hover:opacity-90"
                  aria-label="Scroll to bottom"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
              )}
              </div>
              
              <div className="p-4 border-t">
                <div className="flex gap-2">
                  <Input
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message..."
                    disabled={isLoading}
                    className="flex-1"
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={!inputValue.trim() || isLoading}
                    size="icon"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
